import Button from '../../../common/Button';
import FlexContainer from '../../../common/FlexContainer';
import {
	formFieldHasErrors,
	FormResults,
	genErrorIdFromLabel,
} from '../../../common/Form';
import Spinner from '../../../common/Spinner';
import Typography from '../../../common/Typography';
import { useOperateAttrMutation } from '../../api';
import { mergeErrorStates, extractQueryErrMessage } from '../../api/helpers';
import { AttrActionFormProps } from '../common/commonTypes';
import {
	renderReferenceField,
	renderDerivationSubfields,
} from '../common/jsxHelpers';
import { StyledFlexContainer, StyledPaper } from '../common/styledComponents';
import useAttrNeighbors from '../hooks/useAttrNeighbors';
import {
	mergeAttrFormDefaults,
	mergeAttrFormToPayload,
} from './mergeAttributeHelpers';
import { MergeAttrFormValues } from './mergeAttributeTypes';
import { FunctionComponent, useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

const MergeAttributeForm: FunctionComponent<AttrActionFormProps> = (props) => {
	const { _id, plural: label, updatePopper } = props;

	const formDefaults = mergeAttrFormDefaults();

	const { handleSubmit, register, formState, watch } =
		useForm<MergeAttrFormValues>({
			defaultValues: formDefaults,
		});

	const [watchedDerivationFlag, watchedUsingReferenceValue] = watch([
		'isDerivation',
		'usingStaticReference',
	]);

	// prevent filtering from running every render
	// TODO: eventually we will want to narrow this--just use all neighbors for now
	const filter = useCallback(() => true, []);

	const queryRes = useAttrNeighbors(_id, filter);

	useEffect(() => {
		if (queryRes.isSuccess) {
			updatePopper();
		}
	}, [queryRes.isSuccess, updatePopper]);

	const [mergeAttr, mergeAttrResult] = useOperateAttrMutation();

	const mergedErrs = mergeErrorStates(queryRes, mergeAttrResult);

	const onSubmit: SubmitHandler<MergeAttrFormValues> = (vals, e) => {
		e?.preventDefault();
		mergeAttr({
			attrId: _id,
			body: mergeAttrFormToPayload(vals),
		});
	};

	if (queryRes.isLoading) {
		return (
			<FlexContainer justifyContent="center">
				<Typography paragraph>Loading attributes...</Typography>
				<Spinner />
			</FlexContainer>
		);
	}

	if (queryRes.isError) {
		return (
			<FlexContainer justifyContent="center">
				<Typography color="error" paragraph>
					{extractQueryErrMessage(queryRes.error)}
				</Typography>
			</FlexContainer>
		);
	}

	return (
		<StyledPaper>
			<form onSubmit={handleSubmit(onSubmit)}>
				<StyledFlexContainer flexDirection="column" alignItems="center">
					<Typography id="isDerivation">
						Transformation will replace {label}, derivation will
						create a new attribute
					</Typography>
					<div
						role="radiogroup"
						aria-labelledby="isDerivation"
						aria-errormessage={genErrorIdFromLabel('isDerivation')}
						aria-invalid={formFieldHasErrors(
							'isDerivation',
							formState
						)}
					>
						<div>
							<input
								id="isDerivation-true"
								type="radio"
								value="true"
								{...register('isDerivation', {
									required: {
										value: true,
										message:
											'A value for isDerivation is required',
									},
								})}
							/>
							<label htmlFor="isDerivation-true">
								<Typography>Use derivation</Typography>
							</label>
						</div>
						<div>
							<input
								id="isDerivation-false"
								type="radio"
								value="false"
								{...register('isDerivation', {
									required: {
										value: true,
										message:
											'A value for isDerivation is required',
									},
								})}
							/>
							<label htmlFor="isDerivation-false">
								<Typography>Use transformation</Typography>
							</label>
						</div>
					</div>

					{renderDerivationSubfields(
						watchedDerivationFlag === 'true',
						register,
						formState
					)}

					<Typography id="usingStaticReference">
						Use another attribute or set a static value to merge
						with {label}
					</Typography>
					<div
						role="radiogroup"
						aria-labelledby="usingStaticReference"
						aria-errormessage={genErrorIdFromLabel(
							'usingStaticReference'
						)}
						aria-invalid={formFieldHasErrors(
							'usingStaticReference',
							formState
						)}
					>
						<div>
							<input
								id="dv-false"
								type="radio"
								value="false"
								{...register('usingStaticReference', {
									required: {
										value: true,
										message:
											'A value for usingStaticReference is required',
									},
								})}
							/>
							<label htmlFor="dv-false">
								<Typography>Select an attribute</Typography>
							</label>
						</div>
						<div>
							<input
								id="dv-true"
								type="radio"
								value="true"
								{...register('usingStaticReference', {
									required: {
										value: true,
										message:
											'A value for usingStaticReference is required',
									},
								})}
							/>
							<label htmlFor="dv-true">
								<Typography>Set a static value</Typography>
							</label>
						</div>
					</div>

					{renderReferenceField(
						'otherId',
						'otherValue',
						watchedUsingReferenceValue === 'true',
						register,
						queryRes.attrNeighbors,
						formState
					)}

					<Button type="submit">Submit</Button>

					<FormResults
						isError={!!mergedErrs}
						error={mergedErrs}
						validationErrors={formState.errors}
					/>
				</StyledFlexContainer>
			</form>
		</StyledPaper>
	);
};

export default MergeAttributeForm;
